import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

function getRegisterErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail)) {
    const firstMessage = detail.find((item) => typeof item?.msg === "string")?.msg;
    if (firstMessage) {
      return firstMessage;
    }
  }
  return "Erreur d'inscription";
}

export default function Register() {
  const [form, setForm] = useState({ email: "", password: "", bride_name: "", groom_name: "" });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      navigate("/app");
    } catch (err) {
      toast.error(getRegisterErrorMessage(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-parchment">
      {/* Left Side: Form */}
      <div className="flex items-center justify-center p-8 lg:p-16 order-2 lg:order-1">
        <div className="w-full max-w-md bg-white border border-border-cream p-8 lg:p-12 rounded-sm shadow-sm">
          <form onSubmit={submit} className="space-y-6" data-testid="register-form">
            <div>
              <Link to="/" className="text-xs uppercase tracking-widest font-semibold text-sage hover:text-sage/80 transition-colors flex items-center gap-1">
                ← Retour à l'accueil
              </Link>
              <h1 className="font-serif text-4xl mt-6 text-charcoal font-light">Créons votre mariage</h1>
              <p className="text-xs text-charcoal-soft mt-1 font-light">Quelques détails pour commencer l'aventure.</p>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="overline text-[10px] text-charcoal-soft font-semibold">Marié(e) 1</span>
                  <input 
                    data-testid="register-bride" 
                    name="bride_name"
                    value={form.bride_name} 
                    onChange={set("bride_name")} 
                    autoComplete="given-name"
                    placeholder="Prénom" 
                    className="mt-2 w-full px-4 py-3 bg-parchment border border-border-cream rounded-sm text-sm text-charcoal focus:border-sage focus:outline-none transition-colors" 
                  />
                </label>
                <label className="block">
                  <span className="overline text-[10px] text-charcoal-soft font-semibold">Marié(e) 2</span>
                  <input 
                    data-testid="register-groom" 
                    name="groom_name"
                    value={form.groom_name} 
                    onChange={set("groom_name")} 
                    autoComplete="additional-name"
                    placeholder="Prénom" 
                    className="mt-2 w-full px-4 py-3 bg-parchment border border-border-cream rounded-sm text-sm text-charcoal focus:border-sage focus:outline-none transition-colors" 
                  />
                </label>
              </div>

              <label className="block">
                <span className="overline text-[10px] text-charcoal-soft font-semibold">Adresse Email</span>
                <input 
                  data-testid="register-email" 
                  name="email"
                  required 
                  type="email" 
                  autoComplete="email"
                  value={form.email} 
                  onChange={set("email")} 
                  className="mt-2 w-full px-4 py-3 bg-parchment border border-border-cream rounded-sm text-sm text-charcoal focus:border-sage focus:outline-none transition-colors" 
                />
              </label>

              <label className="block">
                <span className="overline text-[10px] text-charcoal-soft font-semibold">Mot de passe</span>
                <input 
                  data-testid="register-password" 
                  name="password"
                  required 
                  minLength={6} 
                  type="password" 
                  autoComplete="new-password"
                  value={form.password} 
                  onChange={set("password")} 
                  className="mt-2 w-full px-4 py-3 bg-parchment border border-border-cream rounded-sm text-sm text-charcoal focus:border-sage focus:outline-none transition-colors" 
                />
              </label>
            </div>

            <button 
              data-testid="register-submit" 
              disabled={loading} 
              className="btn-sage w-full py-3.5 text-xs font-bold uppercase tracking-widest rounded-sm disabled:opacity-50"
            >
              {loading ? "Création en cours…" : "Créer mon compte"}
            </button>

            <div className="text-xs text-charcoal-soft text-center pt-2 font-light">
              Déjà inscrit ?{" "}
              <Link to="/login" className="underline font-normal text-charcoal hover:text-sage transition-colors">
                Connexion
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* Right Side: Elegant Visual */}
      <div className="hidden lg:block relative overflow-hidden order-1 lg:order-2">
        <img 
          src="https://static.prod-images.emergentagent.com/jobs/c7c691e5-c2e1-48ee-9432-1c4ee8093bb8/images/c56fa28f5223995d9de9252b507206f3f3648579d6af8af7dbe6ac72c493ee63.png" 
          alt="Wedding Elegance" 
          className="w-full h-full object-cover scale-100 hover:scale-102 transition-transform duration-1000" 
        />
        <div className="absolute inset-0 bg-charcoal/10" />
        <div className="absolute bottom-16 left-16 text-white space-y-2">
          <div className="overline text-white/80 tracking-[0.3em] font-semibold text-xs">Ceremind</div>
          <div className="font-serif text-5xl font-light tracking-tight leading-tight">L'écrin de votre projet.</div>
          <div className="text-sm font-light text-white/70 max-w-sm">Planifiez votre jour unique avec l'harmonie et la précision des plus grands événements.</div>
        </div>
      </div>
    </div>
  );
}
